import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { get, isEqual, pick } from 'lodash';
import { combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, groupBy, mergeMap, switchMap, takeWhile, tap } from 'rxjs/operators';

import { hydrationActions } from '../actions/hydration.actions';
import { getUser } from '../selectors/user.selectors';
import { readPersistedSlice } from '../utils/hydration.utils';

@Injectable()
export class HydrationEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);

  hydrate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(hydrationActions.hydrate),
      concatLatestFrom(() => this.store.select(getUser)),
      switchMap(([{ persistedKeys }, user]) => {
        return persistedKeys.map((element) => {
          const userId = user?.id;
          const newState = userId ? readPersistedSlice(element, userId) : null;
          if (newState) {
            return hydrationActions.hydrateSuccess({ element, newState, userId });
          }
          return hydrationActions.hydrateFail({ element, userId });
        });
      })
    )
  );

  persistInLocalstorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(hydrationActions.hydrateSuccess, hydrationActions.hydrateFail, hydrationActions.watch),
        groupBy((action) => action.element.key.join('.')),
        mergeMap((group$) =>
          group$.pipe(
            switchMap((action) =>
              combineLatest([of(action), this.store.select((state) => get(state, action.element.key)), this.store.select(getUser)]).pipe(
                takeWhile(([, , user]) => !!user),
                filter(([{ userId }, , user]) => !!user && userId === user?.id),
                distinctUntilChanged((previous, current) => isEqual(previous[1], current[1])),
                tap(([{ element, userId }, state]) =>
                  localStorage.setItem(`${element.key.join('.')}.${userId}`, JSON.stringify(pick(state, element.sections)))
                )
              )
            )
          )
        )
      ),
    { dispatch: false }
  );
}
